// Cursor-paged option loader for the three filter dimensions whose option lists
// are too large to ship in /filter/filter-hierarchy (sub-customers and cities).
// Talks to /filter/search: page 1 on scope/term change, next page on loadMore().
import { useCallback, useEffect, useRef, useState } from 'react';
import analyticsService from '../../../services/analyticsApi';
import type { useFilters } from '../../../contexts/FiltersContext';
import { buildFilterParams } from './filterParams';

export type SearchDimension = 'subCustomer' | 'originCity' | 'destinationCity';

export interface PagedOption {
  id: string;
  value: string;
}

const PAGE_SIZE = 100;
const SEARCH_DEBOUNCE_MS = 300;

// Query param that carries each dimension's own selection. The backend ignores it
// for that dimension's list, so it must stay out of the scope key: otherwise
// ticking a value would reset the list the user is scrolling.
const OWN_PARAM: Record<SearchDimension, string> = {
  subCustomer: 'customers',
  originCity: 'originCities',
  destinationCity: 'destinationCities',
};

type FilterCtx = ReturnType<typeof useFilters>;

export const buildPagedScope = (
  ctx: FilterCtx,
  dimension: SearchDimension
): Record<string, string[]> => {
  const { [OWN_PARAM[dimension]]: _own, ...scope } = buildFilterParams(ctx);
  return scope;
};

// Cities arrive as bare strings; customers as { id, value } (raw customer_reference
// + "CODE (Name)" label). Only the default "main" customer is shown as "NA"; a
// substring match would also rename real names such as "Maintenance Ltd".
const normalize = (dimension: SearchDimension, raw: string | PagedOption): PagedOption => {
  if (typeof raw === 'string') return { id: raw, value: raw };
  const isMain = dimension === 'subCustomer' && raw.id.toLowerCase() === 'main';
  return { id: raw.id, value: isMain ? 'NA' : raw.value };
};

// Shape of a /filter/search response (only the fields used here).
interface SearchResponse {
  values?: Array<string | PagedOption>;
  hasMore?: boolean;
  nextCursor?: string | null;
  total?: number;
}

interface Options {
  dimension?: SearchDimension;
  scopeParams: Record<string, string[]>;
  term: string;
  enabled: boolean;
}

interface QueryState {
  dimension: SearchDimension;
  q: string;
  scope: Record<string, string[]>;
}

export const usePagedOptions = ({ dimension, scopeParams, term, enabled }: Options) => {
  const [items, setItems] = useState<PagedOption[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [debouncedTerm, setDebouncedTerm] = useState(term.trim());

  const tokenRef = useRef(0);
  const cursorRef = useRef<string | null>(null);
  const inFlightRef = useRef(false);
  const hasMoreRef = useRef(false);
  const queryRef = useRef<QueryState | null>(null);
  const scopeRef = useRef(scopeParams);
  scopeRef.current = scopeParams;
  const scopeKey = JSON.stringify(scopeParams);

  useEffect(() => {
    const t = term.trim();
    if (t === debouncedTerm) return;
    const handle = setTimeout(() => setDebouncedTerm(t), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [term, debouncedTerm]);

  // Drop late responses after unmount.
  useEffect(
    () => () => {
      tokenRef.current += 1;
    },
    []
  );

  const baseParams = (query: QueryState) => ({
    dimension: query.dimension,
    ...(query.q ? { q: query.q } : {}),
    ...query.scope,
  });

  // One page request for both "first page" (cursor null, replaces the items) and
  // "next page" (appends). A token mismatch means the query changed meanwhile, so
  // the response is dropped without touching any state.
  const requestPage = (query: QueryState, token: number, cursor: string | null) => {
    inFlightRef.current = true;
    setLoading(true);

    analyticsService
      .getData(analyticsService.endpoints.filters.search, {
        ...baseParams(query),
        ...(cursor ? { cursor } : {}),
        limit: PAGE_SIZE,
      })
      .then(({ data }: { data: SearchResponse }) => {
        if (tokenRef.current !== token) return;
        const next = data?.nextCursor ?? null;
        cursorRef.current = next;
        hasMoreRef.current = Boolean(data?.hasMore) && next !== null;
        const page = (data?.values ?? []).map((v) => normalize(query.dimension, v));
        setItems((prev) => {
          if (!cursor) return page;
          const seen = new Set(prev.map((p) => p.id));
          return [...prev, ...page.filter((p) => !seen.has(p.id))];
        });
        setHasMore(hasMoreRef.current);
      })
      .catch(() => {
        if (tokenRef.current !== token) return;
        hasMoreRef.current = false;
        setHasMore(false);
        setError('Failed to load options');
      })
      .finally(() => {
        if (tokenRef.current !== token) return;
        inFlightRef.current = false;
        setLoading(false);
      });
  };

  // Reset + page 1 + total whenever the scope, the term or the dimension changes.
  useEffect(() => {
    if (!enabled || !dimension) return;
    const token = ++tokenRef.current;
    const query: QueryState = { dimension, q: debouncedTerm, scope: scopeRef.current };
    queryRef.current = query;
    cursorRef.current = null;
    hasMoreRef.current = false;
    setItems([]);
    setTotal(null);
    setHasMore(false);
    setError(null);

    requestPage(query, token, null);

    // The count is a second scan on the backend: never block the list on it.
    analyticsService
      .getData(analyticsService.endpoints.filters.search, {
        ...baseParams(query),
        limit: 1,
        withTotal: true,
      })
      .then(({ data }: { data: SearchResponse }) => {
        if (tokenRef.current !== token) return;
        if (typeof data?.total === 'number') setTotal(data.total);
      })
      .catch(() => {
        // Total is cosmetic; the list works without it.
      });
    // scopeKey stands in for scopeParams (read through scopeRef); requestPage only
    // touches refs and state setters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, dimension, scopeKey, debouncedTerm]);

  const loadMore = useCallback(() => {
    const query = queryRef.current;
    const cursor = cursorRef.current;
    if (!query || !cursor || !hasMoreRef.current || inFlightRef.current) return;
    requestPage(query, tokenRef.current, cursor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { items, total, hasMore, loading, loadMore, error };
};
